import { useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import {
    BuildingsIcon,
    FolderOpenIcon,
    FolderUserIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { AutoHideOnScroll, Badge, toast } from '@/components';
import { SidePanel } from '@/components/SidePanel';
import { ActionType } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import {
    OverlayNames,
    revalidateSourceFolders,
    useOpenInNewWindow,
    useOverlayManager,
    usePermissions,
} from '@/hooks';
import { Permission } from '@/types/permissions';
import { FullSource, SourcesSelection, SourceType } from '@/types/source';
import { EngineService } from '@/services/engine';
import { cleanupAfterSourceDeletion } from '@/stores';
import { revalidateIdentityViews } from '@/utils/swr';
import { useSourceCreationStore } from '@/stores/source-creation.store';
import { useCameraLimitReached } from '@/features/SettingsView/hooks/useCameraLimitReached';
import {
    revalidateAlertUnitCapacity,
    revalidateOrganizationConfiguration,
} from '@/features/SettingsView/hooks/useConfiguration';
import { useSourcesFilters } from './hooks/useSourcesFilters';
import { useFolderData } from './hooks/useFolderData';
import { useFolderSort } from './hooks/useFolderSort';
import { useFolderOperations } from './hooks/useFolderOperations';
import {
    selectionVisibility,
    useSourceGridData,
} from './hooks/useSourceGridData';
import { useSyncQueryPanelFolderScope } from './hooks/useSyncQueryPanelFolderScope';
import { SourcesActionBar } from './components/SourcesActionBar';
import { SourcesSidebar } from './components/SourcesSidebar';
import { SourceGridResults } from './components/SourceGridResults';
import { CreateFolderModal } from './components/CreateFolderModal';
import { DeleteFolderConfirmationModal } from './components/DeleteFolderConfirmationModal';
import { DeleteSourceConfirmationModal } from '@/features/Source/components/DeleteSourceConfirmationModal';
import { EditSourceModal } from '@/features/Source/components/EditSourceModal';
import { ReanalyzeSourceConfirmationModal } from '@/features/Source/components/ReanalyzeSourceConfirmationModal';
import { useSourceReanalysis } from '@/features/Source/hooks/useSourceReanalysis';
import {
    ManageFoldersModal,
    type FolderMembershipIntent,
} from './components/ManageFoldersModal';
import type { SourcesNavProps } from './components/SourcesSidebar';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useSourcesStore } from '@/stores/sources.store';
import { isHdmiViewport } from '@/utils/source';
import { revalidateSourceList } from '@/hooks/useSourceListInfinite';
import { useGridMultiSelection } from '@/hooks/useGridMultiSelection';

export function SourceGrid() {
    const { t } = useTranslation(I18nNamespace.SOURCES);
    const { canPerform } = usePermissions();
    const isCameraLimitReached = useCameraLimitReached();
    const { mutate } = useSWRConfig();
    const { openUrl } = useOpenInNewWindow();
    const { open } = useOverlayManager();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<
        { ids: string[]; name?: string } | { described: true } | null
    >(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [searchDraft, setSearchDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
    const [sourcePendingEditSnapshot, setSourcePendingEditSnapshot] =
        useState<FullSource | null>(null);
    const [sourcePendingReanalysis, setSourcePendingReanalysis] =
        useState<FullSource | null>(null);
    const [manageFoldersModal, setManageFoldersModal] = useState<{
        sources: FullSource[];
        described?: true;
    } | null>(null);
    const fileItems = useSourceCreationStore((state) => state.fileItems);
    const updateSource = useSourcesStore((state) => state.updateSource);
    const cameraItems = useSourceCreationStore((state) => state.cameraItems);
    const { reanalyzeSource, isStartingAnalysis } = useSourceReanalysis();

    const {
        myFoldersSort,
        setMyFoldersSort,
        sharedFoldersSort,
        setSharedFoldersSort,
    } = useFolderSort();

    const {
        selection,
        setSelection,
        sourceGroups,
        myFolders,
        sharedFolders,
        allSourcesCount,
        sharedSourcesCount,
        groupsLoading,
        hasMoreMyFolders,
        hasMoreSharedFolders,
        isLoadingMoreMyFolders,
        isLoadingMoreSharedFolders,
        loadMoreMyFolders,
        loadMoreSharedFolders,
    } = useFolderData(fileItems, cameraItems, myFoldersSort, sharedFoldersSort);

    const selectedFolderId = selection.kind === 'folder' ? selection.id : null;
    const isFolderScope = selection.kind === 'folder';
    const selectedFolder = isFolderScope
        ? sourceGroups.find((group) => group.id === selectedFolderId)
        : undefined;
    const isSelectedFolderResolved = !isFolderScope || !!selectedFolder;
    const isSharedSelection =
        selection.kind === 'organization' ||
        (isFolderScope && selectedFolder?.isOwner === false);

    useSyncQueryPanelFolderScope(selection);

    const {
        typeFilter,
        sortOption,
        setTypeFilter,
        setSortOption,
        getScopeParams,
        getQueryParams: getFilterParams,
    } = useSourcesFilters();

    const {
        displaySources,
        folderChildren,
        totalSources,
        hasMore,
        isLoadingMore,
        isInitialLoading,
        reset: resetSourceData,
        loadMore,
    } = useSourceGridData({
        selection,
        searchQuery,
        typeFilter,
        sortOption,
        fileItems,
        getFilterParams,
    });

    const {
        folderToEdit,
        setFolderToEdit,
        folderToDelete,
        setFolderToDelete,
        isDeletingFolder,
        folderDeleteSourceCount,
        handleEditFolder,
        handleDeleteFolder,
        handleDeleteFolderConfirm,
    } = useFolderOperations({
        selectedFolderId,
        clearFolderSelection: () => setSelection({ kind: 'private' }),
        folderChildren,
    });

    const {
        selection: sourceSelection,
        isMultiSelectMode,
        selectedCount,
        isDescribed,
        isSelected,
        toggleSelected: toggleSource,
        selectAll,
        selectAllMatching,
        clearSelection,
        exitMultiSelectMode,
        enterMultiSelectMode,
    } = useGridMultiSelection();

    useEffect(() => {
        exitMultiSelectMode();
    }, [selection, exitMultiSelectMode]);

    const handleSelectionChange = (next: SourcesSelection) => {
        if (
            (next.kind === 'private' && selection.kind === 'private') ||
            (next.kind === 'organization' &&
                selection.kind === 'organization') ||
            (next.kind === 'folder' &&
                selection.kind === 'folder' &&
                next.id === selection.id)
        ) {
            return;
        }
        setSelection(next);
        resetSourceData();
    };

    const submitSearch = (value: string) => {
        if (value !== searchQuery) {
            clearSelection();
            resetSourceData();
            setSearchQuery(value);
        }
    };

    const changeTypeFilter = (type: typeof typeFilter) => {
        if (type === typeFilter) return;
        clearSelection();
        resetSourceData();
        setTypeFilter(type);
    };

    const changeSortOption = (option: typeof sortOption) => {
        if (
            option?.field === sortOption?.field &&
            option?.direction === sortOption?.direction
        )
            return;
        clearSelection();
        resetSourceData();
        setSortOption(option);
    };

    const selectableSources = displaySources.filter(
        (source) => !isHdmiViewport(source.id),
    );

    const selectableTotal = isFolderScope
        ? selectableSources.length
        : totalSources;

    const toggleSelectAll = () => {
        if (selectedCount > 0 && selectedCount === selectableTotal) {
            clearSelection();
            return;
        }
        if (isFolderScope) {
            selectAll(selectableSources.map((source) => source.id));
            return;
        }

        selectAllMatching({
            selectedAt: Math.floor(Date.now() / 1000),
            total: selectableTotal,
        });
    };

    const selectedSources = displaySources.filter(isSelected);

    const manageSelectedFolders = () => {
        if (isDescribed) {
            setManageFoldersModal({ sources: [], described: true });
            return;
        }
        if (selectedSources.length === 0) return;
        setManageFoldersModal({ sources: selectedSources });
    };

    const deleteSelected = () => {
        setDeleteConfirm(
            isDescribed
                ? { described: true }
                : { ids: selectedSources.map((source) => source.id) },
        );
    };
    const describedScopeIsOwned = selection.kind === 'private';
    const isPermittedOnSelection = (permission: Permission) =>
        isDescribed
            ? describedScopeIsOwned && canPerform(permission, true)
            : selectedSources.length > 0 &&
              selectedSources.every((source) =>
                  canPerform(permission, source.isOwner),
              );

    const bulkActionDefinitions = [
        {
            permission: Permission.EDIT,
            icon: FolderOpenIcon,
            label: t('actions.manageFolders'),
            onClick: manageSelectedFolders,
        },
        {
            permission: Permission.DELETE,
            icon: TrashIcon,
            label: t('actions.delete'),
            onClick: deleteSelected,
        },
    ];

    const selectionBulkActions: ActionConfig[] = bulkActionDefinitions
        .filter(({ permission }) => isPermittedOnSelection(permission))
        .map(({ icon, label, onClick }) => ({
            type: ActionType.ITEM as const,
            icon,
            label,
            onClick,
        }));

    const canMultiSelect =
        isSelectedFolderResolved &&
        !isSharedSelection &&
        bulkActionDefinitions.some(({ permission }) =>
            canPerform(permission, true),
        );

    const onSourceClick = (
        sourceId: string,
        event?: MouseEvent<HTMLDivElement>,
    ) => {
        if (!isMultiSelectMode) {
            openUrl(`/sources/${sourceId}`, event, {
                preserveSearchParams: false,
            });
            return;
        }
        if (!isHdmiViewport(sourceId)) {
            toggleSource(sourceId);
        }
    };

    const requestDeleteSource = (sourceId: string) => {
        const source = displaySources.find((item) => item.id === sourceId);
        if (source) {
            setDeleteConfirm({ ids: [sourceId], name: source.displayName });
        }
    };

    const requestEditSource = (sourceId: string) => {
        const source = displaySources.find((item) => item.id === sourceId);
        if (!source) {
            return;
        }
        setSourcePendingEditSnapshot(source);
    };

    const requestReanalyzeSource = (sourceId: string) => {
        const source = displaySources.find((item) => item.id === sourceId);
        if (!source) return;
        setSourcePendingReanalysis(source);
    };

    const confirmReanalysis = async () => {
        if (!sourcePendingReanalysis) return;
        await reanalyzeSource(sourcePendingReanalysis);
        setSourcePendingReanalysis(null);
    };

    const handleEditSourceModalClose = () => {
        setSourcePendingEditSnapshot(null);
    };

    const handleEditSourceSubmit = async (name: string) => {
        if (!sourcePendingEditSnapshot) {
            return;
        }

        try {
            await EngineService.updateSource(sourcePendingEditSnapshot.id, {
                name,
            });
            updateSource(sourcePendingEditSnapshot.id, { name });

            await revalidateSourceList();
            await mutate(
                (key) =>
                    Array.isArray(key) &&
                    key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN,
            );

            toast.success(t('toast.editSuccess'));
        } catch (error) {
            toast.error(t('toast.editError'));
            throw error;
        }
    };

    const confirmDelete = async () => {
        if (!deleteConfirm || isDeleting) return;
        setIsDeleting(true);
        const described = 'described' in deleteConfirm;
        const isBulk = described || deleteConfirm.ids.length > 1;
        const deletedSourceIds = described
            ? selectableSources.filter(isSelected).map((source) => source.id)
            : deleteConfirm.ids;
        try {
            const {
                deleted: deletedCount,
                failed: failedCount,
                inProgress,
            } = await EngineService.deleteSources(
                described
                    ? {
                          selectAll: true,
                          selectedAt:
                              sourceSelection.mode === 'all'
                                  ? sourceSelection.selectedAt
                                  : Math.floor(Date.now() / 1000),
                          excludeIds:
                              sourceSelection.mode === 'all'
                                  ? Array.from(sourceSelection.excluded)
                                  : [],
                      }
                    : { ids: deleteConfirm.ids },
                described
                    ? {
                          visibility:
                              selectionVisibility(selection) ?? undefined,
                          search: searchQuery || undefined,
                          ...getScopeParams(),
                      }
                    : undefined,
            );

            if (failedCount === 0 && !inProgress) {
                deletedSourceIds.forEach(cleanupAfterSourceDeletion);
            }

            await revalidateIdentityViews();
            await revalidateSourceList();
            await revalidateSourceFolders();
            await revalidateOrganizationConfiguration();
            await revalidateAlertUnitCapacity();
            if (selectedFolderId) {
                await mutate(
                    (key) =>
                        Array.isArray(key) &&
                        key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN &&
                        key[1] === selectedFolderId,
                );
            }

            if (inProgress) {
                toast.info(t('toast.deleteSelectedInProgress'));
            } else if (failedCount > 0 && deletedCount > 0) {
                toast.error(
                    t('toast.deleteSelectedPartial', {
                        deleted: deletedCount,
                        failed: failedCount,
                    }),
                );
            } else if (failedCount > 0) {
                toast.error(
                    t(
                        isBulk
                            ? 'toast.deleteSelectedError'
                            : 'toast.deleteError',
                    ),
                );
            } else {
                toast.success(
                    t(
                        isBulk
                            ? 'toast.deleteSelectedSuccess'
                            : 'toast.deleteSuccess',
                    ),
                );
            }
            setDeleteConfirm(null);
            exitMultiSelectMode();
        } catch {
            toast.error(
                t(isBulk ? 'toast.deleteSelectedError' : 'toast.deleteError'),
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const openManageFolders = (sourceId: string) => {
        const source = displaySources.find((s) => s.id === sourceId);
        if (!source) return;
        setManageFoldersModal({ sources: [source] });
    };

    const confirmManageFolders = async (intent: FolderMembershipIntent) => {
        if (!manageFoldersModal) return;
        const described = manageFoldersModal.described === true;
        try {
            await EngineService.updateSourceFolderMemberships(
                {
                    ...intent,
                    selection: described
                        ? {
                              selectAll: true,
                              selectedAt:
                                  sourceSelection.mode === 'all'
                                      ? sourceSelection.selectedAt
                                      : Math.floor(Date.now() / 1000),
                              excludeIds:
                                  sourceSelection.mode === 'all'
                                      ? Array.from(sourceSelection.excluded)
                                      : [],
                          }
                        : {
                              ids: manageFoldersModal.sources.map((s) => s.id),
                          },
                },
                described
                    ? {
                          visibility:
                              selectionVisibility(selection) ?? undefined,
                          search: searchQuery || undefined,
                          ...getScopeParams(),
                      }
                    : undefined,
            );
            await revalidateSourceFolders();
            await revalidateSourceList();
            await mutate(
                (key) =>
                    Array.isArray(key) &&
                    key[0] === CACHE_KEYS.SOURCE_FOLDER_CHILDREN,
            );
            setManageFoldersModal(null);
            toast.success(t('folders.manageFoldersSuccess'));
        } catch {
            toast.error(t('folders.manageFoldersError'));
        } finally {
            exitMultiSelectMode();
        }
    };

    const folderHeaderTitle =
        selection.kind === 'folder'
            ? (selectedFolder?.name ?? t('folders.mySources'))
            : selection.kind === 'organization'
              ? t('folders.sharedWithMe')
              : t('folders.mySources');

    const isSharedByOwnerSelection =
        selectedFolder?.isOwner === true && selectedFolder?.isShared === true;

    const folderHeaderIcon = isSharedSelection ? (
        <FolderUserIcon />
    ) : (
        <FolderOpenIcon />
    );

    const navProps: SourcesNavProps = {
        myFolders,
        sharedFolders,
        allSourcesCount,
        sharedSourcesCount,
        selection,
        onSelectionChange: handleSelectionChange,
        typeFilter,
        onTypeFilterClear: () => changeTypeFilter('all'),
        onCreateFolderClick: () => setIsCreateFolderOpen(true),
        onEditFolder: handleEditFolder,
        onDeleteFolder: handleDeleteFolder,
        myFoldersSortOption: myFoldersSort,
        onMyFoldersSortChange: setMyFoldersSort,
        sharedFoldersSortOption: sharedFoldersSort,
        onSharedFoldersSortChange: setSharedFoldersSort,
        isLoadingGroups: groupsLoading,
        hasMoreMyFolders,
        onLoadMoreMyFolders: loadMoreMyFolders,
        isLoadingMoreMyFolders,
        hasMoreSharedFolders,
        onLoadMoreSharedFolders: loadMoreSharedFolders,
        isLoadingMoreSharedFolders,
    };

    return (
        <div className="relative h-full min-h-0">
            <SidePanel side="left">
                <SourcesSidebar navProps={navProps} />
            </SidePanel>
            <div className="h-full flex flex-col relative">
                <AutoHideOnScroll container={scrollContainer}>
                    <SourcesActionBar
                        isMultiSelectMode={isMultiSelectMode}
                        selectedCount={selectedCount}
                        totalCount={selectableTotal}
                        searchValue={searchDraft}
                        onSearchChange={setSearchDraft}
                        onSearchSubmit={submitSearch}
                        searchPlaceholder={t('menu.searchPlaceholder')}
                        onExitMultiSelect={exitMultiSelectMode}
                        onSelectAll={toggleSelectAll}
                        enterMultiSelectMode={
                            canMultiSelect ? enterMultiSelectMode : undefined
                        }
                        selectionBulkActions={selectionBulkActions}
                        typeFilter={typeFilter}
                        onTypeFilterChange={changeTypeFilter}
                        sortOption={sortOption}
                        onSortOptionChange={changeSortOption}
                        selectedFolder={selectedFolder ?? null}
                        onEditFolder={handleEditFolder}
                        onDeleteFolder={handleDeleteFolder}
                        onCreateFolder={() => setIsCreateFolderOpen(true)}
                    />
                </AutoHideOnScroll>
                <div
                    ref={setScrollContainer}
                    className="flex-1 overflow-y-auto scrollbar-hide min-h-0 pb-20 px-4 panels:px-8"
                >
                    <div className="w-full max-w-content mx-auto pt-20">
                        <SourceGridResults
                            folderConfig={{
                                headerTitle: folderHeaderTitle,
                                headerIcon: folderHeaderIcon,
                                headerAccessory: isSharedByOwnerSelection ? (
                                    <Badge variant="base" className="shrink-0">
                                        <BuildingsIcon
                                            className="size-3 text-grey-200"
                                            weight="fill"
                                        />
                                        <span className="text-xs-medium">
                                            {t('folders.sharedBadge')}
                                        </span>
                                    </Badge>
                                ) : undefined,
                                currentFolderId: selectedFolderId,
                                onManageFolders: openManageFolders,
                            }}
                            sources={displaySources}
                            isSourceSelected={isSelected}
                            isMultiSelectMode={isMultiSelectMode}
                            onToggleSource={onSourceClick}
                            onEditSource={requestEditSource}
                            onReanalyzeSource={requestReanalyzeSource}
                            onDeleteSource={requestDeleteSource}
                            onLoadMore={loadMore}
                            isLoadingMore={isLoadingMore}
                            hasMore={hasMore}
                            scrollContainer={scrollContainer}
                            isInitialLoading={isInitialLoading}
                            onAddSourceFile={() =>
                                open(OverlayNames.SOURCE_UPLOAD, {
                                    targetFolderId:
                                        selectedFolderId ?? undefined,
                                })
                            }
                            onAddSourceCamera={() =>
                                open(OverlayNames.ADD_CAMERA, {
                                    targetFolderId:
                                        selectedFolderId ?? undefined,
                                })
                            }
                            isCameraLimitReached={isCameraLimitReached}
                        />
                    </div>
                </div>
                <DeleteSourceConfirmationModal
                    isOpen={!!deleteConfirm}
                    count={
                        deleteConfirm && 'described' in deleteConfirm
                            ? selectedCount
                            : deleteConfirm?.ids.length
                    }
                    deleteInProgress={isDeleting}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteConfirm(null)}
                />
                <EditSourceModal
                    isOpen={sourcePendingEditSnapshot !== null}
                    onClose={handleEditSourceModalClose}
                    initialName={sourcePendingEditSnapshot?.name ?? ''}
                    lockExtension={
                        sourcePendingEditSnapshot?.type === SourceType.FILE
                    }
                    onSubmit={handleEditSourceSubmit}
                />
                <ReanalyzeSourceConfirmationModal
                    isOpen={sourcePendingReanalysis !== null}
                    submitInProgress={isStartingAnalysis}
                    onConfirm={() => void confirmReanalysis()}
                    onCancel={() => setSourcePendingReanalysis(null)}
                />
                <CreateFolderModal
                    isOpen={isCreateFolderOpen || folderToEdit !== null}
                    onClose={() => {
                        setIsCreateFolderOpen(false);
                        setFolderToEdit(null);
                    }}
                    initialFolder={folderToEdit}
                    onSuccess={(group, isEdit) => {
                        setFolderToEdit(null);
                        if (!isEdit) {
                            setSelection({ kind: 'folder', id: group.id });
                        }
                        toast.success(
                            isEdit
                                ? t('folders.renameFolderSuccess')
                                : t('folders.createFolderSuccess'),
                        );
                    }}
                />
                <DeleteFolderConfirmationModal
                    isOpen={folderToDelete !== null}
                    deleteInProgress={isDeletingFolder}
                    sourceCount={folderDeleteSourceCount}
                    onConfirm={handleDeleteFolderConfirm}
                    onCancel={() => setFolderToDelete(null)}
                />
                {manageFoldersModal && (
                    <ManageFoldersModal
                        isOpen
                        sources={manageFoldersModal.sources}
                        describedCount={
                            manageFoldersModal.described
                                ? selectedCount
                                : undefined
                        }
                        onClose={() => setManageFoldersModal(null)}
                        onSubmit={confirmManageFolders}
                    />
                )}

                <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-t from-[rgb(17,18,20)] to-transparent" />
            </div>
        </div>
    );
}
